"use client"

import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from "react"
import { suggestDomains } from "@/lib/utils/email"

export interface EmailInputProps {
  value: string
  onChange: (value: string) => void
  /** wrapper div className */
  className?: string
  /** the <input> className (style it to match the surface) */
  inputClassName?: string
  /** inline style for the <input> (for sites that style with style={} not className) */
  inputStyle?: CSSProperties
  placeholder?: string
  id?: string
  name?: string
  required?: boolean
  disabled?: boolean
  autoFocus?: boolean
  onBlur?: () => void
  /** Forwarded for keys EmailInput does not consume for its dropdown (e.g. Enter-to-submit). */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
  style?: CSSProperties
  "aria-invalid"?: boolean
  "aria-label"?: string
  "aria-describedby"?: string
}

/**
 * Email field with anticipated common-domain suggestions (gmail.com, …).
 * Controlled + self-contained: works on any surface; style via inputClassName.
 */
export function EmailInput({
  value,
  onChange,
  className,
  inputClassName,
  inputStyle,
  placeholder,
  id,
  name,
  required,
  disabled,
  autoFocus,
  onBlur,
  onKeyDown: onKeyDownProp,
  style,
  "aria-invalid": ariaInvalid,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedby,
}: EmailInputProps) {
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(-1)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current) }, [])

  const suggestions = suggestDomains(value)
  const showList = open && suggestions.length > 0

  function select(s: string) {
    onChange(s)
    setOpen(false)
    setHighlight(-1)
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (showList) {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setHighlight((h) => (h + 1) % suggestions.length)
        return
      }
      if (e.key === "ArrowUp") {
        e.preventDefault()
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length)
        return
      }
      if (e.key === "Enter" && highlight >= 0) {
        e.preventDefault()
        select(suggestions[highlight])
        return
      }
      if (e.key === "Escape") {
        setOpen(false)
        setHighlight(-1)
        return
      }
    }
    onKeyDownProp?.(e)
  }

  return (
    <div className={className} style={{ position: "relative", ...style }}>
      <input
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        role="combobox"
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={highlight >= 0 ? `${listId}-opt-${highlight}` : undefined}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
        id={id}
        name={name}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className={inputClassName}
        style={inputStyle}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setOpen(true)
          setHighlight(-1)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={() => {
          if (blurTimer.current) clearTimeout(blurTimer.current)
          blurTimer.current = setTimeout(() => setOpen(false), 120)
          onBlur?.()
        }}
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-xl"
          style={{
            background: "var(--redy-surface)",
            border: "1px solid var(--redy-border)",
            boxShadow: "0 10px 30px -12px rgba(26,26,26,0.25)",
          }}
        >
          {suggestions.map((s, i) => (
            <li
              key={s}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => {
                e.preventDefault()
                select(s)
              }}
              onMouseEnter={() => setHighlight(i)}
              className="cursor-pointer px-3.5 py-2.5 text-sm"
              style={{
                color: "var(--redy-charcoal)",
                background: i === highlight ? "var(--redy-surface-2)" : "var(--redy-surface)",
              }}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
